import { FC, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import * as K from 'common/constants';
import { useLocation } from 'react-router-dom';
import { getUrlParam } from 'common/util/location';
import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertConfig, PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { clearGraphQLErrorMessage } from 'common/util/string';
import { EMAIL_CONFIRMATION_MUTATION, GET_EMAIL_QUERY, VERIFY_EMAIL_MUTATION } from './queries';

import './styles.scss';

type AccountData = {
  email: string;
  isEmailVerified: boolean;
};

const VerifyEmailPage: FC = () => {
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState('');
  const { data: accountData } = useQuery<{ account: AccountData }>(GET_EMAIL_QUERY);
  const [showPage, setShowPage] = useState(false);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const setEmailConfirmationPageAlert = (
    messageAndVariantSettings: Pick<PageAlertConfig, 'message' | 'variant'>,
  ): void => {
    setPageAlertConfig({
      ...messageAndVariantSettings,
      autoHide: true,
      duration: 10,
    });
  };

  const [sendConfirmation] = useMutation(EMAIL_CONFIRMATION_MUTATION, {
    onCompleted: (data: { requestEmailConfirmation: boolean }) => {
      if (data.requestEmailConfirmation) {
        setEmailConfirmationPageAlert({
          message: `A confirmation e-mail has been sent to ${accountData?.account.email}.`,
          variant: PageAlertVariant.SUCCESS,
        });

        return;
      }

      setEmailConfirmationPageAlert({
        message: `Your e-mail address, ${accountData?.account.email}, was already confirmed.`,
        variant: PageAlertVariant.WARNING,
      });
    },
    onError: (error: ApolloError) => {
      setEmailConfirmationPageAlert({
        message: clearGraphQLErrorMessage(error.message),
        variant: PageAlertVariant.ERROR,
      });
    },
  });

  const [verifyToken] = useMutation(VERIFY_EMAIL_MUTATION, {
    onCompleted: (data: { verifyEmail: boolean }) => {
      if (data.verifyEmail) {
        window.location.href = K.ROUTE.LOG_START;
      } else {
        setEmailConfirmationPageAlert({
          message: `Your e-mail address, ${accountData?.account.email}, was already confirmed.`,
          variant: PageAlertVariant.WARNING,
        });
        setShowPage(true);
      }
    },
    onError: (error: ApolloError) => {
      setErrorMessage(clearGraphQLErrorMessage(error.message));
      setShowPage(true);
    },
  });

  useEffect(() => {
    // TODO: Fix once we upgrade react-router to next major version. There is an incompatibility between history and react-router
    const search = location.search || location.location?.search;
    const token = getUrlParam(search || '', K.URL_PARAM.TOKEN);
    if (token) {
      verifyToken({
        variables: { token },
      });
    } else {
      setShowPage(true);
    }
  }, []);

  const handleConfirmationEmailSend = (): void => {
    sendConfirmation();
  };

  return showPage ? (
    <Page docTitle="Verify E-mail Address">
      <PageHeader title="Confirm E-mail Address" />
      {errorMessage && <p className="VerifyEmail__Text">{errorMessage}</p>}
      <p className="VerifyEmail__Text">
        To receive a new confirmation email,
        <Button className="VerifyEmail__Link" variant={ButtonVariant.BORDERLESS} onClick={handleConfirmationEmailSend}>
          click here.
        </Button>
      </p>
    </Page>
  ) : (
    <></>
  );
};

export default VerifyEmailPage;
